import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bell, Bot, CalendarDays, Check, Clock3, DollarSign, Eye, FileText, Flag, Gift, LayoutDashboard, LogOut,
  ImagePlus, Mail, Megaphone, Radio, RefreshCw, RotateCcw, Save, Search, Share2,
  ShieldCheck, Tag, Ticket, Trophy, Upload, Users, Square, Trash2, WalletCards, X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import BrandLogo from "@/components/BrandLogo";
import IvsPlaybackLayer from "@/components/IvsPlaybackLayer";
import SocialMediaCommandCenter from "@/components/admin/SocialMediaCommandCenter";
import { formatDateMMDDYear } from "@/lib/eventTime";
import { getWithTransientRetry } from "@/utils/apiRetry";
import { canCreatorDeleteEvent } from "@/utils/eventDeletion";

const money = (value) => `$${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const payoutStatusLabel = (event, revenue, held) => {
  if (event.payout_processed) return "Released";
  if (held > 0) return "Refund hold";
  if (revenue <= 0) return "No revenue";
  return {
    pending_stripe_setup: "Stripe setup required",
    waiting_for_funds: "Waiting for Stripe funds",
    needs_payment_review: "Payment review required",
    failed: "Transfer failed",
    processing: "Processing",
    completed: "Released",
  }[event.payout_status] || "Auto queued";
};

const dateTime = (value) => value ? new Date(value).toLocaleString(undefined, {
  month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
}) : "Not scheduled";

const adminAssetUrl = (value) => {
  if (!value || /^https?:\/\//i.test(value)) return value || "";
  const backendOrigin = String(axiosInstance.defaults.baseURL || "").replace(/\/api\/?$/, "");
  return `${backendOrigin}${value.startsWith("/") ? value : `/${value}`}`;
};

const NAV_GROUPS = [
  {
    label: "Operations",
    items: [
      { key: "overview", label: "Overview", icon: LayoutDashboard },
      { key: "events", label: "Events", icon: CalendarDays },
      { key: "monitor", label: "Live Monitor", icon: Eye },
      { key: "refunds", label: "Approvals", icon: ShieldCheck },
      { key: "payouts", label: "Payouts", icon: WalletCards },
      { key: "promos", label: "Promo Codes", icon: Tag },
    ],
  },
  {
    label: "Community",
    items: [
      { key: "creators", label: "Creators", icon: Users },
      { key: "reports", label: "Reports", icon: FileText },
      { key: "automations", label: "Automations", icon: Bot },
    ],
  },
  {
    label: "Growth",
    items: [
      { key: "social", label: "Social Command Center", icon: Share2 },
    ],
  },
];

const NAV_ITEMS = NAV_GROUPS.flatMap((group) => group.items);

const EMPTY_PROMO = {
  code: "",
  description: "",
  discount_type: "percentage",
  discount_value: "",
  applies_to: "all",
  max_uses: "",
  expiration_date: "",
};

const EMAIL_AUTOMATIONS = [
  {
    key: "welcome_email",
    enabledKey: "welcome_emails_enabled",
    title: "Automatic welcome emails",
    detail: "Send a branded welcome email immediately after a new account completes sign-up.",
    icon: Mail,
    tokens: "{name}",
  },
  {
    key: "monthly_creator_report",
    enabledKey: "monthly_reports_enabled",
    title: "Monthly creator reports",
    detail: "Send each creator their previous month's events, ticket counts, and revenue.",
    icon: FileText,
    tokens: "{name}, {month}, {event_count}, {revenue}",
  },
  {
    key: "campaigns",
    enabledKey: "campaigns_enabled",
    title: "Campaigns",
    detail: "Blast an editable flyer and promotional message to every eligible ShowMeLive account.",
    icon: Megaphone,
    tokens: "{name}",
  },
  {
    key: "top_monthly_viewers",
    enabledKey: "top_monthly_viewers_enabled",
    title: "Top Monthly Viewers",
    detail: "Recognize the top 10 ShowMeTithes senders and top 10 ShowMeTips senders each month.",
    icon: Trophy,
    tokens: "{name}, {month}, {rank_summary}",
  },
];

const StatCard = ({ label, value, detail, accent = "text-white", featured = false }) => (
  <div className={`rounded-2xl border p-5 ${featured
    ? "border-blue-500/30 bg-gradient-to-br from-blue-600/20 to-blue-600/[.04]"
    : "border-white/[.08] bg-[#141416]"}`}
  >
    <p className={`mb-2 text-[11px] font-bold uppercase tracking-[.1em] ${featured ? "text-blue-300" : "text-gray-400"}`}>{label}</p>
    <p className={`text-2xl font-black tracking-tight sm:text-[28px] ${accent}`}>{value}</p>
    {detail && <p className="mt-1.5 text-xs text-gray-500">{detail}</p>}
  </div>
);

const Section = ({ title, detail, children, action }) => (
  <section className="overflow-hidden rounded-[18px] border border-white/[.08] bg-[#121214]">
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/[.06] px-5 py-4">
      <div>
        <h2 className="text-base font-bold text-white">{title}</h2>
        {detail && <p className="mt-1 text-xs text-gray-500">{detail}</p>}
      </div>
      {action}
    </header>
    {children}
  </section>
);

const StatusBadge = ({ active, activeLabel = "Active", inactiveLabel = "Inactive" }) => (
  <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${active
    ? "bg-green-500/15 text-green-400"
    : "bg-gray-500/15 text-gray-400"}`}
  >
    {active ? activeLabel : inactiveLabel}
  </span>
);

const AutomationCard = ({ icon: Icon, title, detail, enabled, footer, onToggle, busy, testId }) => (
  <div className="rounded-2xl border border-white/[.08] bg-[#141416] p-5">
    <div className="flex items-start justify-between gap-4">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400"><Icon className="h-5 w-5" /></span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={`Turn ${title} ${enabled ? "off" : "on"}`}
        onClick={onToggle}
        disabled={busy}
        className={`relative h-7 w-12 rounded-full transition-colors ${enabled ? "bg-blue-600" : "bg-gray-700"} disabled:opacity-50`}
        data-testid={testId}
      >
        <span className={`absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-5" : "translate-x-0"}`} />
      </button>
    </div>
    <h3 className="mt-5 font-bold">{title}</h3>
    <p className="mt-2 min-h-10 text-xs leading-relaxed text-gray-400">{detail}</p>
    <p className="mt-4 border-t border-white/[.06] pt-4 text-xs font-semibold text-gray-300">{footer}</p>
  </div>
);

const EmailTemplateEditor = ({
  automation,
  draft,
  busy,
  onChange,
  onSave,
  onUpload,
  onRemoveFlyer,
  onSendCampaign,
  campaignBusy,
}) => {
  const Icon = automation.icon;
  const flyerUrl = draft?.flyer_url;
  return (
    <article className="overflow-hidden rounded-2xl border border-white/[.08] bg-[#141416]" data-testid={`email-editor-${automation.key}`}>
      <div className="flex items-start gap-3 border-b border-white/[.06] p-5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400"><Icon className="h-5 w-5" /></span>
        <div className="min-w-0">
          <h3 className="font-bold text-white">{automation.title}</h3>
          <p className="mt-1 text-[11px] leading-relaxed text-gray-500">Available fields: {automation.tokens}</p>
        </div>
      </div>
      <div className="space-y-4 p-5">
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold text-gray-300">Email subject</span>
          <input
            value={draft?.subject || ""}
            onChange={(event) => onChange("subject", event.target.value)}
            className="admin-field"
            maxLength={200}
            data-testid={`email-subject-${automation.key}`}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold text-gray-300">Email message</span>
          <textarea
            value={draft?.message || ""}
            onChange={(event) => onChange("message", event.target.value)}
            className="admin-field min-h-32 resize-y py-3"
            maxLength={10000}
            data-testid={`email-message-${automation.key}`}
          />
        </label>
        <div>
          <span className="mb-1.5 block text-xs font-bold text-gray-300">Flyer</span>
          {flyerUrl ? (
            <div className="overflow-hidden rounded-xl border border-white/10 bg-black/30">
              <img src={adminAssetUrl(flyerUrl)} alt={`${automation.title} flyer`} className="max-h-52 w-full object-contain" />
              <div className="flex items-center justify-between gap-3 border-t border-white/[.06] p-3">
                <span className="truncate text-[11px] text-green-300">Flyer attached</span>
                <button type="button" onClick={onRemoveFlyer} disabled={busy} className="text-xs font-bold text-red-300 hover:text-red-200">Remove</button>
              </div>
            </div>
          ) : (
            <div className="flex min-h-24 items-center justify-center rounded-xl border border-dashed border-white/15 bg-white/[.02] text-xs text-gray-500">
              <ImagePlus className="mr-2 h-4 w-4" /> No flyer uploaded
            </div>
          )}
          <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[.04] px-3 py-2.5 text-xs font-bold text-gray-200 transition hover:bg-white/[.08]">
            <Upload className="h-4 w-4" /> {flyerUrl ? "Replace flyer" : "Upload flyer"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onUpload(file);
                event.target.value = "";
              }}
              data-testid={`email-flyer-${automation.key}`}
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onSave}
            disabled={busy || !draft?.subject?.trim() || !draft?.message?.trim()}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-blue-500 disabled:opacity-50"
            data-testid={`save-email-template-${automation.key}`}
          >
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save email"}
          </button>
          {automation.key === "campaigns" && (
            <button
              type="button"
              onClick={onSendCampaign}
              disabled={campaignBusy || busy}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-green-500 disabled:opacity-50"
              data-testid="send-campaign-now"
            >
              <Megaphone className="h-4 w-4" /> {campaignBusy ? "Queuing…" : "Send to everyone"}
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => (
    new URLSearchParams(window.location.search).has("social")
      ? "social"
      : (new URLSearchParams(window.location.search).has("monitor") ? "monitor" : "overview")
  ));
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [viewerBans, setViewerBans] = useState([]);
  const [promos, setPromos] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [automations, setAutomations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creatorQuery, setCreatorQuery] = useState("");
  const [eventQuery, setEventQuery] = useState("");
  const [eventStatus, setEventStatus] = useState("all");
  const [payingId, setPayingId] = useState(null);
  const [promoForm, setPromoForm] = useState(EMPTY_PROMO);
  const [promoBusy, setPromoBusy] = useState(false);
  const [refundBusy, setRefundBusy] = useState(null);
  const [automationBusy, setAutomationBusy] = useState(null);
  const [emailTemplateDrafts, setEmailTemplateDrafts] = useState({});
  const [emailTemplateBusy, setEmailTemplateBusy] = useState(null);
  const [campaignBusy, setCampaignBusy] = useState(false);
  const [eventBusy, setEventBusy] = useState(null);
  const [deletedRecoveryEmail, setDeletedRecoveryEmail] = useState("");
  const [deletedRecoveryDate, setDeletedRecoveryDate] = useState("");
  const [deletedRecoveryTitle, setDeletedRecoveryTitle] = useState("");
  const [deletedRecoveryBusy, setDeletedRecoveryBusy] = useState(false);
  const [deletedRecovery, setDeletedRecovery] = useState(null);
  const [unbanBusy, setUnbanBusy] = useState(null);
  const [financialRecovery, setFinancialRecovery] = useState(null);
  const [financialRecoveryBusy, setFinancialRecoveryBusy] = useState(false);
  const [financialRecoveryResult, setFinancialRecoveryResult] = useState(null);
  const [stripeBalanceDiagnostic, setStripeBalanceDiagnostic] = useState(null);
  const [historicalFinancialRecovery, setHistoricalFinancialRecovery] = useState(null);
  const [legacyRecovery, setLegacyRecovery] = useState(null);
  const [legacyRecoveryBusy, setLegacyRecoveryBusy] = useState(false);
  const [legacyRecoveryResult, setLegacyRecoveryResult] = useState(null);
  const [legacyRecoveryArmed, setLegacyRecoveryArmed] = useState(false);
  const [recoveredReports, setRecoveredReports] = useState([]);
  const [contentReports, setContentReports] = useState([]);
  const [monitoredEventId, setMonitoredEventId] = useState("");
  const [creatorPendingDelete, setCreatorPendingDelete] = useState(null);
  const [creatorDeleteConfirmation, setCreatorDeleteConfirmation] = useState("");
  const [creatorDeleteBusy, setCreatorDeleteBusy] = useState(false);
  const [resourceLoading, setResourceLoading] = useState({});
  const loadedResources = useRef({});
  const resourceRequests = useRef({});

  const loadResource = async (resource, force = false) => {
    if (!force && loadedResources.current[resource]) return;
    if (resourceRequests.current[resource]) {
      if (!force) return resourceRequests.current[resource];
      // A mutation-triggered refresh must not reuse a request that started
      // before the mutation completed.
      await resourceRequests.current[resource];
    }

    const endpoints = {
      dashboard: "/admin/dashboard",
      users: "/admin/users",
      events: "/admin/events",
      promos: "/admin/promo-codes",
      refunds: "/admin/refunds",
      automations: "/admin/automations",
      viewerBans: "/admin/viewer-bans",
      recoveredReports: "/admin/recovered-reports",
      contentReports: "/admin/content-reports",
      historicalFinancialRecovery: "/admin/historical-financial-recovery",
      stripeBalanceDiagnostic: "/admin/stripe/balance-diagnostic",
    };
    const request = (async () => {
      setResourceLoading((current) => ({ ...current, [resource]: true }));
      try {
        // Admin resources are read-only here. A brief Atlas socket timeout
        // should retry instead of rendering valid records as an empty table.
        const response = await getWithTransientRetry(axiosInstance, endpoints[resource]);
        if (resource === "dashboard") {
          const dashboardData = response.data;
          if (!dashboardData || typeof dashboardData !== "object" || !dashboardData.users || !dashboardData.events || !dashboardData.revenue) {
            throw new Error("Invalid administrator overview response");
          }
          setStats(dashboardData);
        }
        if (["users", "events", "promos", "refunds", "viewerBans", "recoveredReports", "contentReports"].includes(resource) && !Array.isArray(response.data)) {
          throw new Error(`Invalid administrator ${resource} response`);
        }
        if (resource === "users") setUsers(response.data);
        if (resource === "events") setEvents(response.data);
        if (resource === "promos") setPromos(response.data);
        if (resource === "refunds") setRefunds(response.data);
        if (resource === "viewerBans") setViewerBans(response.data);
        if (resource === "recoveredReports") setRecoveredReports(response.data);
        if (resource === "contentReports") setContentReports(response.data);
        if (resource === "stripeBalanceDiagnostic") setStripeBalanceDiagnostic(response.data);
        if (resource === "historicalFinancialRecovery") {
          if (!response.data || typeof response.data !== "object" || Array.isArray(response.data)) {
            throw new Error("Invalid historical financial recovery response");
          }
          setHistoricalFinancialRecovery(response.data);
        }
        if (resource === "automations") {
          const automationData = response.data || null;
          if (!automationData || typeof automationData !== "object" || Array.isArray(automationData)) {
            throw new Error("Invalid administrator automations response");
          }
          setAutomations(automationData);
          setEmailTemplateDrafts(automationData?.email_templates || {});
        }
        loadedResources.current[resource] = true;
      } catch (requestError) {
        if ([401, 403].includes(requestError?.response?.status)) {
          navigate("/admin/login");
          return;
        }
        setError(`Failed to load ${resource === "dashboard" ? "the admin overview" : `admin ${resource}`}. Please try again.`);
      } finally {
        setResourceLoading((current) => ({ ...current, [resource]: false }));
        delete resourceRequests.current[resource];
      }
    })();
    resourceRequests.current[resource] = request;
    return request;
  };

  const load = async () => {
    setLoading(true);
    setError("");
    await loadResource("dashboard", true);
    setLoading(false);

    // Populate the overview details after its key figures are already visible.
    // Promo, automation, and moderation data wait until their tabs are opened.
    void Promise.allSettled([
      loadResource("users"),
      loadResource("events"),
      loadResource("refunds"),
    ]);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    const resourcesByTab = {
      events: ["events"],
      monitor: ["events", "contentReports"],
      payouts: ["events", "refunds", "dashboard", "historicalFinancialRecovery", "stripeBalanceDiagnostic"],
      refunds: ["refunds", "events"],
      reports: ["events", "refunds", "viewerBans", "recoveredReports", "contentReports"],
      automations: ["automations", "users"],
      creators: ["users"],
      promos: ["promos"],
    };
    (resourcesByTab[activeTab] || []).forEach((resource) => {
      void loadResource(resource);
    });
    /* eslint-disable-next-line */
  }, [activeTab]);

  const creators = useMemo(() => users.filter((account) => account.role !== "admin"), [users]);
  const filteredCreators = useMemo(() => creators.filter((creator) => (
    !creatorQuery || `${creator.username || ""} ${creator.name || ""} ${creator.email || ""}`
      .toLowerCase().includes(creatorQuery.toLowerCase())
  )), [creatorQuery, creators]);
  const filteredEvents = useMemo(() => events.filter((event) => {
    const status = String(event.status || "scheduled").toLowerCase();
    const matchesStatus = eventStatus === "all" || status === eventStatus;
    const searchText = [
      event.title,
      event.id,
      event.creator?.name,
      event.creator?.email,
    ].filter(Boolean).join(" ").toLowerCase();
    return matchesStatus && (!eventQuery || searchText.includes(eventQuery.toLowerCase()));
  }), [eventQuery, eventStatus, events]);
  const reportCountByEvent = useMemo(() => {
    const counts = {};
    contentReports.forEach((report) => {
      if (report.event_id) counts[report.event_id] = Number(counts[report.event_id] || 0) + 1;
    });
    return counts;
  }, [contentReports]);
  const liveMonitorEvents = useMemo(() => events.filter((event) => (
    String(event.status || "").toLowerCase() === "live"
  )), [events]);
  const monitoredEvent = useMemo(() => events.find((event) => (
    String(event.id) === String(monitoredEventId)
  )) || null, [events, monitoredEventId]);

  useEffect(() => {
    if (activeTab !== "monitor" || monitoredEventId || liveMonitorEvents.length === 0) return;
    setMonitoredEventId(liveMonitorEvents[0].id);
  }, [activeTab, liveMonitorEvents, monitoredEventId]);
  const pendingPayouts = useMemo(() => events.filter((event) => (
    !event.payout_processed && Number(event.total_revenue || 0) > 0
  )), [events]);
  const pendingPayoutTotal = pendingPayouts.reduce((total, event) => total + Number(
    event.available_creator_earnings ?? (Number(event.total_revenue || 0) * 0.8)
  ), 0);
  const pendingRefunds = useMemo(() => refunds.filter((item) => item.status === "pending"), [refunds]);
  const heldRefundTotal = pendingRefunds.reduce((total, item) => total + Number(item.amount || 0), 0);

  const logout = () => {
    try { localStorage.removeItem("session_token"); } catch (_) {}
    navigate("/admin/login");
  };

  const payout = async (event) => {
    const creatorAmount = Number(event.available_creator_earnings ?? (Number(event.total_revenue || 0) * 0.8));
    if (!window.confirm(`Use the backup payout to release ${money(creatorAmount)} for "${event.title}"?`)) return;
    setPayingId(event.id);
    try {
      await axiosInstance.post(`/admin/events/${event.id}/payout`);
      await Promise.all([
        loadResource("events", true),
        loadResource("dashboard", true),
      ]);
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Payout failed.");
    } finally {
      setPayingId(null);
    }
  };

  const previewFinancialRecovery = async () => {
    setFinancialRecoveryBusy(true);
    setFinancialRecoveryResult(null);
    try {
      const response = await axiosInstance.get("/admin/financial-recovery/stripe");
      setFinancialRecovery(response.data);
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Financial recovery preview failed.");
    } finally {
      setFinancialRecoveryBusy(false);
    }
  };

  const applyFinancialRecovery = async () => {
    if (!financialRecovery?.safe_to_apply || !financialRecovery?.fingerprint) return;
    if (!window.confirm("Restore the verified Stripe payment and payout history to ShowMeLive? This does not move money or create Stripe transfers.")) return;
    setFinancialRecoveryBusy(true);
    try {
      const response = await axiosInstance.post("/admin/financial-recovery/stripe", {
        fingerprint: financialRecovery.fingerprint,
      });
      setFinancialRecoveryResult(response.data);
      await Promise.all([
        loadResource("events", true),
        loadResource("dashboard", true),
      ]);
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Financial recovery failed.");
    } finally {
      setFinancialRecoveryBusy(false);
    }
  };

  const previewLegacyRecovery = async () => {
    setLegacyRecoveryBusy(true);
    setLegacyRecoveryResult(null);
    setLegacyRecoveryArmed(false);
    try {
      const response = await axiosInstance.get("/admin/legacy-recovery");
      setLegacyRecovery(response.data);
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Data recovery preview failed.");
    } finally {
      setLegacyRecoveryBusy(false);
    }
  };

  const applyLegacyRecovery = async () => {
    if (!legacyRecovery?.can_apply || !legacyRecovery?.fingerprint) return;
    if (!legacyRecoveryArmed) {
      setLegacyRecoveryArmed(true);
      return;
    }
    setLegacyRecoveryBusy(true);
    try {
      const response = await axiosInstance.post("/admin/legacy-recovery", {
        fingerprint: legacyRecovery.fingerprint,
      });
      setLegacyRecoveryResult(response.data);
      setLegacyRecovery(null);
      setLegacyRecoveryArmed(false);
      await Promise.all([
        loadResource("promos", true),
        loadResource("recoveredReports", true),
        loadResource("events", true),
        loadResource("automations", true),
      ]);
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Data recovery failed.");
    } finally {
      setLegacyRecoveryBusy(false);
    }
  };

  const decideRefund = async (refund, decision) => {
    const verb = decision === "approve" ? "refund the viewer" : "return the held amount to the creator";
    if (!window.confirm(`${decision === "approve" ? "Approve" : "Reject"} ${money(refund.amount)} and ${verb}?`)) return;
    setRefundBusy(`${refund.id}:${decision}`);
    try {
      await axiosInstance.post(`/admin/refunds/${refund.id}/${decision}`);
      await Promise.all([
        loadResource("refunds", true),
        loadResource("events", true),
        loadResource("dashboard", true),
      ]);
      setActiveTab("refunds");
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || `Failed to ${decision} refund.`);
    } finally {
      setRefundBusy(null);
    }
  };

  const toggleAutomation = async (key) => {
    if (!automations) return;
    setAutomationBusy(key);
    try {
      const response = await axiosInstance.patch("/admin/automations", { [key]: !automations[key] });
      setAutomations(response.data);
      setEmailTemplateDrafts((current) => Object.fromEntries(
        Object.entries(response.data?.email_templates || {}).map(([automationKey, template]) => [
          automationKey,
          {
            ...template,
            subject: current[automationKey]?.subject ?? template.subject,
            message: current[automationKey]?.message ?? template.message,
          },
        ]),
      ));
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to update automation.");
    } finally {
      setAutomationBusy(null);
    }
  };

  const setPromoField = (field, value) => setPromoForm((current) => ({ ...current, [field]: value }));

  const createPromo = async (event) => {
    event.preventDefault();
    setPromoBusy(true);
    try {
      await axiosInstance.post("/admin/promo-codes", {
        code: promoForm.code.trim().toUpperCase(),
        description: promoForm.description,
        discount_type: promoForm.discount_type,
        discount_value: Number(promoForm.discount_value),
        applies_to: promoForm.applies_to,
        max_uses: promoForm.max_uses ? Number(promoForm.max_uses) : null,
        expiration_date: promoForm.expiration_date ? new Date(`${promoForm.expiration_date}T23:59:59`).toISOString() : null,
      });
      setPromoForm(EMPTY_PROMO);
      await loadResource("promos", true);
      setActiveTab("promos");
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to create promo code.");
    } finally {
      setPromoBusy(false);
    }
  };

  const togglePromo = async (promo) => {
    try {
      await axiosInstance.put(`/admin/promo-codes/${promo.id}`, { is_active: !promo.is_active });
      await loadResource("promos", true);
      setActiveTab("promos");
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to update promo code.");
    }
  };

  const deletePromo = async (promo) => {
    if (!window.confirm(`Delete promo code "${promo.code}"?`)) return;
    try {
      await axiosInstance.delete(`/admin/promo-codes/${promo.id}`);
      await loadResource("promos", true);
      setActiveTab("promos");
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to delete promo code.");
    }
  };

  const endAdminEvent = async (event) => {
    if (!window.confirm(`Terminate "${event.title}" now? This immediately disconnects the creator and every viewer and marks the event completed.`)) return;
    setEventBusy(`${event.id}:end`);
    try {
      await axiosInstance.post(`/admin/events/${event.id}/end`);
      await Promise.all([
        loadResource("events", true),
        loadResource("dashboard", true),
      ]);
      setActiveTab("events");
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to terminate the event.");
    } finally {
      setEventBusy(null);
    }
  };

  const policyTerminateAdminEvent = async (event) => {
    const reason = window.prompt(
      `Policy reason for terminating "${event.title}"? The creator will be unable to create or start events for 30 days, but may continue watching other creators.`,
      "Policy and Terms violation",
    );
    if (!reason?.trim()) return;
    if (!window.confirm(`Terminate "${event.title}" now and suspend this creator's event privileges for 30 days?`)) return;
    setEventBusy(`${event.id}:policy`);
    try {
      await axiosInstance.post(`/admin/events/${event.id}/policy-terminate`, { reason: reason.trim() });
      await Promise.all([
        loadResource("events", true),
        loadResource("contentReports", true),
        loadResource("dashboard", true),
      ]);
      setMonitoredEventId("");
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to apply the policy termination.");
    } finally {
      setEventBusy(null);
    }
  };

  const deleteAdminEvent = async (event) => {
    const isLive = String(event.status || "").toLowerCase() === "live";
    const warning = isLive
      ? `"${event.title}" is live. End its stream and permanently delete the event? This cannot be undone.`
      : `Permanently delete "${event.title}"? This cannot be undone.`;
    if (!window.confirm(warning)) return;
    setEventBusy(`${event.id}:delete`);
    try {
      if (isLive) await axiosInstance.post(`/admin/events/${event.id}/end`);
      await axiosInstance.delete(`/admin/events/${event.id}`);
      await Promise.all([
        loadResource("events", true),
        loadResource("dashboard", true),
      ]);
      setActiveTab("events");
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to delete the event.");
    } finally {
      setEventBusy(null);
    }
  };

  const inspectDeletedEvents = async () => {
    if (!deletedRecoveryEmail.trim()) return;
    setDeletedRecoveryBusy(true);
    setDeletedRecovery(null);
    try {
      const response = await axiosInstance.get("/admin/events/deleted-recovery-candidates", {
        params: {
          creator_email: deletedRecoveryEmail.trim(),
          deleted_date: deletedRecoveryDate || undefined,
          event_title: deletedRecoveryTitle.trim() || undefined,
        },
      });
      setDeletedRecovery(response.data);
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Deleted-event inspection failed.");
    } finally {
      setDeletedRecoveryBusy(false);
    }
  };

  const restoreDeletedEvent = async (candidate) => {
    if (!window.confirm(`Restore "${deletedRecoveryTitle}" from its retained production ledger?`)) return;
    setDeletedRecoveryBusy(true);
    try {
      const response = await axiosInstance.post("/admin/events/deleted-recovery-restore", {
        creator_email: deletedRecoveryEmail.trim(),
        event_id: candidate.event_id,
        title: deletedRecoveryTitle.trim(),
        category: "religion",
        event_date: deletedRecoveryDate,
        timezone: "America/Chicago",
      });
      window.alert(`Event restored. Retained gross: ${money(response.data?.event?.total_revenue)}.`);
      await Promise.all([loadResource("events", true), loadResource("dashboard", true)]);
      setDeletedRecovery(null);
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Deleted-event restore failed.");
    } finally {
      setDeletedRecoveryBusy(false);
    }
  };

  const unbanViewer = async (ban) => {
    const viewerName = ban.viewer?.username
      ? `@${String(ban.viewer.username).replace(/^@/, "")}`
      : (ban.viewer?.name || ban.viewer?.email || "this viewer");
    const creatorName = ban.creator?.name || ban.creator?.email || "this creator";
    if (!window.confirm(`Restore ${viewerName}'s access to all current and future events from ${creatorName}?`)) return;
    const busyKey = `${ban.creator_id}:${ban.user_id}`;
    setUnbanBusy(busyKey);
    try {
      await axiosInstance.delete(`/admin/viewer-bans/${encodeURIComponent(ban.creator_id)}/${encodeURIComponent(ban.user_id)}`);
      setViewerBans((current) => current.filter(
        (item) => `${item.creator_id}:${item.user_id}` !== busyKey,
      ));
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to unban the viewer.");
    } finally {
      setUnbanBusy(null);
    }
  };

  const updateEmailDraft = (automationKey, field, value) => {
    setEmailTemplateDrafts((current) => ({
      ...current,
      [automationKey]: {
        ...(current[automationKey] || {}),
        [field]: value,
      },
    }));
  };

  const applyAutomationResponse = (data, preserveDraftCopy = false) => {
    setAutomations(data);
    if (!preserveDraftCopy) {
      setEmailTemplateDrafts(data?.email_templates || {});
      return;
    }
    setEmailTemplateDrafts((current) => Object.fromEntries(
      Object.entries(data?.email_templates || {}).map(([automationKey, template]) => [
        automationKey,
        {
          ...template,
          subject: current[automationKey]?.subject ?? template.subject,
          message: current[automationKey]?.message ?? template.message,
        },
      ]),
    ));
  };

  const saveEmailTemplate = async (automationKey) => {
    const draft = emailTemplateDrafts[automationKey];
    if (!draft?.subject?.trim() || !draft?.message?.trim()) return;
    setEmailTemplateBusy(`${automationKey}:save`);
    try {
      const response = await axiosInstance.put(`/admin/automations/email-templates/${automationKey}`, {
        subject: draft.subject.trim(),
        message: draft.message.trim(),
      });
      applyAutomationResponse(response.data);
      return true;
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to save the email.");
      return false;
    } finally {
      setEmailTemplateBusy(null);
    }
  };

  const uploadEmailFlyer = async (automationKey, file) => {
    setEmailTemplateBusy(`${automationKey}:flyer`);
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await axiosInstance.post(
        `/admin/automations/email-templates/${automationKey}/flyer`,
        form,
      );
      applyAutomationResponse(response.data, true);
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to upload the flyer.");
    } finally {
      setEmailTemplateBusy(null);
    }
  };

  const removeEmailFlyer = async (automationKey) => {
    if (!window.confirm("Remove this flyer from the email automation?")) return;
    setEmailTemplateBusy(`${automationKey}:flyer`);
    try {
      const response = await axiosInstance.delete(`/admin/automations/email-templates/${automationKey}/flyer`);
      applyAutomationResponse(response.data, true);
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to remove the flyer.");
    } finally {
      setEmailTemplateBusy(null);
    }
  };

  const sendCampaign = async () => {
    const eligibleAccounts = users.filter((account) => (
      account.role !== "admin" && account.email && !account.is_blocked
    )).length;
    if (!window.confirm(`Send this campaign to ${eligibleAccounts.toLocaleString()} eligible account${eligibleAccounts === 1 ? "" : "s"}?`)) return;
    setCampaignBusy(true);
    try {
      const saved = await saveEmailTemplate("campaigns");
      if (!saved) return;
      const response = await axiosInstance.post("/admin/automations/campaigns/send");
      window.alert(`Campaign queued for ${Number(response.data?.audience_count || 0).toLocaleString()} recipients.`);
      const refresh = await axiosInstance.get("/admin/automations");
      applyAutomationResponse(refresh.data);
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to queue the campaign.");
    } finally {
      setCampaignBusy(false);
    }
  };

  const openCreatorDelete = (creator) => {
    setCreatorPendingDelete(creator);
    setCreatorDeleteConfirmation("");
  };

  const closeCreatorDelete = () => {
    if (creatorDeleteBusy) return;
    setCreatorPendingDelete(null);
    setCreatorDeleteConfirmation("");
  };

  const deleteCreatorProfile = async () => {
    if (!creatorPendingDelete) return;
    setCreatorDeleteBusy(true);
    try {
      const response = await axiosInstance.delete(`/admin/users/${creatorPendingDelete.id}`, {
        data: { confirmation_email: creatorDeleteConfirmation.trim() },
      });
      const retainedEvents = Number(response.data?.retained_event_count || 0);
      setCreatorPendingDelete(null);
      setCreatorDeleteConfirmation("");
      await Promise.all([
        loadResource("users", true),
        loadResource("dashboard", true),
      ]);
      setActiveTab("creators");
      window.alert(`Creator profile deleted. ${retainedEvents} historical event${retainedEvents === 1 ? "" : "s"} retained for reports.`);
    } catch (requestError) {
      window.alert(requestError?.response?.data?.detail || "Failed to delete the creator profile.");
    } finally {
      setCreatorDeleteBusy(false);
    }
  };

  if (loading && !stats) {
    return <div className="flex min-h-screen items-center justify-center bg-[#0f0f0f] text-gray-400">Loading admin console…</div>;
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-white/[.08] bg-gradient-to-b from-[#141414] to-[#0f0f0f]">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between gap-4 px-4 sm:px-7">
          <div className="flex min-w-0 items-center">
            <div className="flex shrink-0 items-center gap-2.5">
              <BrandLogo imageClassName="h-9 w-[108px] object-contain sm:w-[122px]" />
              <span className="rounded-md border border-white/15 px-2 py-1 text-[9px] font-extrabold uppercase tracking-[.18em] text-gray-500">Admin</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <span className="relative hidden sm:inline-flex">
              <Bell className="h-5 w-5 text-gray-300" />
              {pendingRefunds.length > 0 && <span className="absolute -right-2 -top-2 min-w-4 rounded-full bg-red-500 px-1 text-center text-[10px] font-extrabold">{pendingRefunds.length}</span>}
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-blue-900 text-sm font-bold">A</div>
            <button onClick={logout} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-white/5 hover:text-white" data-testid="admin-logout" aria-label="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-4 pb-2 md:hidden" aria-label="Admin sections mobile">
          {NAV_ITEMS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setActiveTab(key)} aria-current={activeTab === key ? "page" : undefined} className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold ${activeTab === key ? "bg-blue-600/20 text-blue-300" : "text-gray-400"}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
              {key === "refunds" && pendingRefunds.length > 0 && <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[9px] text-white">{pendingRefunds.length}</span>}
            </button>
          ))}
        </nav>
      </header>

      <div className="mx-auto flex max-w-[1600px] items-start">
        <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-[272px] shrink-0 border-r border-white/[.07] bg-[#101012] px-4 py-7 md:block">
          <nav aria-label="Admin sections" className="space-y-7">
            {NAV_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="mb-2 px-3 text-[10px] font-black uppercase tracking-[.18em] text-gray-600">{group.label}</p>
                <div className="space-y-1">
                  {group.items.map(({ key, label, icon: Icon }) => {
                    const selected = activeTab === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setActiveTab(key)}
                        className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition-colors ${selected ? "bg-[#17233f] text-white" : "text-gray-400 hover:bg-white/[.04] hover:text-white"}`}
                        data-testid={`admin-nav-${key}`}
                        aria-current={selected ? "page" : undefined}
                      >
                        <Icon className={`h-[18px] w-[18px] shrink-0 ${selected ? "text-blue-400" : "text-gray-500"}`} />
                        <span className="min-w-0 flex-1">{label}</span>
                        {key === "refunds" && pendingRefunds.length > 0 && (
                          <span className="flex min-h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-black text-white">{pendingRefunds.length}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1 px-4 py-8 sm:px-7 sm:py-10 lg:px-9">
        {error && <div className="mb-6 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

        {activeTab === "overview" && stats && (
          <div className="space-y-7" data-testid="admin-overview-section">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight sm:text-[26px]">Platform overview</h1>
                <p className="mt-1 text-sm text-gray-400">Live figures across every ShowMeLive event and creator.</p>
              </div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs font-semibold text-blue-300">
                <ShieldCheck className="h-3.5 w-3.5" /> Admin verified
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
              <StatCard featured label="Platform earnings" value={money(stats.revenue.platform_earnings)} detail="20% platform share" />
              <StatCard label="Creator payouts queued" value={money(pendingPayoutTotal)} detail={`${pendingPayouts.length} event${pendingPayouts.length === 1 ? "" : "s"} awaiting payout`} accent="text-green-400" />
              <StatCard label="Pending refunds" value={pendingRefunds.length} detail={`${money(heldRefundTotal)} held from event profit`} accent="text-red-400" />
              <StatCard label="Live events" value={stats.events.live} detail={`${stats.events.total} total events`} accent="text-blue-400" />
              <StatCard label="Active accounts" value={Math.max(0, stats.users.total - stats.users.blocked)} detail={`${stats.users.blocked} blocked`} />
            </div>

            <div className="grid gap-5 xl:grid-cols-2">
              <Section title="Recent creators" detail={`${creators.length} creator-capable accounts`} action={
                <button onClick={() => setActiveTab("creators")} className="text-xs font-semibold text-blue-400 hover:text-blue-300">View directory</button>
              }>
                <div className="divide-y divide-white/[.05]">
                  {creators.slice(0, 6).map((creator, index) => (
                    <div key={creator.id} className="grid grid-cols-[28px_1fr_auto] items-center gap-3 px-5 py-3 hover:bg-white/[.02]">
                      <span className="font-bold text-gray-600">{index + 1}</span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{creator.username ? `@${creator.username.replace(/^@/, "")}` : creator.name}</p>
                        <p className="truncate text-xs text-gray-500">{creator.email}</p>
                      </div>
                      <StatusBadge active={!creator.is_blocked} activeLabel="Active" inactiveLabel="Blocked" />
                    </div>
                  ))}
                  {creators.length === 0 && <p className="px-5 py-8 text-center text-sm text-gray-500">No creator accounts yet.</p>}
                </div>
              </Section>

              <Section title="Event operations" detail={`${events.length} events · ${stats.tickets.total} tickets sold`} action={
                <button onClick={() => setActiveTab("events")} className="text-xs font-semibold text-blue-400 hover:text-blue-300">Manage events</button>
              }>
                <div className="divide-y divide-white/[.05]">
                  {events.slice(0, 6).map((event) => (
                    <div key={event.id} className="grid grid-cols-[1fr_auto] items-center gap-3 px-5 py-3 hover:bg-white/[.02]">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{event.title}</p>
                        <p className="mt-1 text-xs capitalize text-gray-500">{event.status} · {event.ticket_count || 0} tickets</p>
                      </div>
                      <p className="text-sm font-bold text-green-400">{money(event.total_revenue)}</p>
                    </div>
                  ))}
                  {events.length === 0 && <p className="px-5 py-8 text-center text-sm text-gray-500">No events yet.</p>}
                </div>
              </Section>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
              <button onClick={() => setActiveTab("payouts")} className="rounded-2xl border border-white/[.08] bg-[#141416] p-5 text-left transition hover:-translate-y-0.5 hover:border-green-500/30">
                <WalletCards className="mb-4 h-9 w-9 rounded-xl bg-green-500/10 p-2 text-green-400" />
                <h3 className="font-bold">Creator payouts</h3><p className="mt-1 text-xs leading-relaxed text-gray-400">Creators receive 80% of gross receipts. ShowMeLive covers Stripe processing fees from its 20% share.</p>
              </button>
              <button onClick={() => setActiveTab("creators")} className="rounded-2xl border border-white/[.08] bg-[#141416] p-5 text-left transition hover:-translate-y-0.5 hover:border-blue-500/30">
                <Users className="mb-4 h-9 w-9 rounded-xl bg-blue-500/10 p-2 text-blue-400" />
                <h3 className="font-bold">Creator directory</h3><p className="mt-1 text-xs leading-relaxed text-gray-400">Search account identities, emails, roles, and account status.</p>
              </button>
              <button onClick={() => setActiveTab("promos")} className="rounded-2xl border border-white/[.08] bg-[#141416] p-5 text-left transition hover:-translate-y-0.5 hover:border-purple-500/30">
                <Gift className="mb-4 h-9 w-9 rounded-xl bg-purple-500/10 p-2 text-purple-400" />
                <h3 className="font-bold">Promo codes</h3><p className="mt-1 text-xs leading-relaxed text-gray-400">Create rewards and manage active ticket or Pro Mode discounts.</p>
              </button>
              <button onClick={() => setActiveTab("refunds")} className="rounded-2xl border border-white/[.08] bg-[#141416] p-5 text-left transition hover:-translate-y-0.5 hover:border-red-500/30">
                <RotateCcw className="mb-4 h-9 w-9 rounded-xl bg-red-500/10 p-2 text-red-400" />
                <h3 className="font-bold">Refund queue</h3><p className="mt-1 text-xs leading-relaxed text-gray-400">Review held viewer refunds and release each decision safely.</p>
              </button>
              <button onClick={() => setActiveTab("automations")} className="rounded-2xl border border-white/[.08] bg-[#141416] p-5 text-left transition hover:-translate-y-0.5 hover:border-cyan-500/30">
                <Bot className="mb-4 h-9 w-9 rounded-xl bg-cyan-500/10 p-2 text-cyan-400" />
                <h3 className="font-bold">Automations</h3><p className="mt-1 text-xs leading-relaxed text-gray-400">Control email campaigns, monthly reports, viewer rewards, and post-show payouts.</p>
              </button>
            </div>
          </div>
        )}

        {activeTab === "monitor" && (
          <div className="space-y-6" data-testid="admin-live-monitor-section">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl font-extrabold">Live Event Monitor</h1>
                <p className="mt-1 text-sm text-gray-400">Private IVS monitoring does not join viewer presence, increment attendance, or notify the event.</p>
              </div>
              <button
                type="button"
                onClick={() => Promise.all([loadResource("events", true), loadResource("contentReports", true)])}
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[.04] px-3 py-2 text-xs font-bold text-gray-300 hover:bg-white/[.08]"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </button>
            </div>

            <Section
              title={monitoredEvent ? monitoredEvent.title : "Select a live event"}
              detail={monitoredEvent
                ? `${monitoredEvent.creator?.name || "Creator"} · ${reportCountByEvent[monitoredEvent.id] || 0} viewer report(s)`
                : "The direct AWS IVS preview appears here."}
              action={monitoredEvent && String(monitoredEvent.status || "").toLowerCase() === "live" ? (
                <button
                  type="button"
                  onClick={() => policyTerminateAdminEvent(monitoredEvent)}
                  disabled={Boolean(eventBusy)}
                  className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-red-500 disabled:opacity-50"
                  data-testid={`policy-terminate-${monitoredEvent.id}`}
                >
                  <Square className="h-4 w-4" /> {eventBusy === `${monitoredEvent.id}:policy` ? "Terminating…" : "Terminate for policy violation"}
                </button>
              ) : null}
            >
              <div className="p-4">
                <div className="relative mx-auto aspect-video max-h-[62vh] min-h-[260px] w-full overflow-hidden rounded-2xl bg-black">
                  {monitoredEvent ? (
                    <IvsPlaybackLayer
                      key={monitoredEvent.id}
                      eventId={monitoredEvent.id}
                      orientation={monitoredEvent.orientation || "portrait"}
                      statusEndpoint={`/admin/events/${encodeURIComponent(monitoredEvent.id)}/monitor`}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">No live event selected</div>
                  )}
                </div>
              </div>
            </Section>

            <Section title="Live events" detail={`${liveMonitorEvents.length} event(s) available for private monitoring`}>
              <div className="divide-y divide-white/[.06]">
                {liveMonitorEvents.map((event) => (
                  <button
                    type="button"
                    key={event.id}
                    onClick={() => setMonitoredEventId(event.id)}
                    className={`flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-white/[.04] ${String(monitoredEventId) === String(event.id) ? "bg-blue-500/10" : ""}`}
                    data-testid={`monitor-event-${event.id}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-white">{event.title || "Untitled event"}</span>
                      <span className="mt-1 block truncate text-xs text-gray-500">{event.creator?.name || "Unknown creator"} · {event.creator?.email || "No email"}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {Number(reportCountByEvent[event.id] || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2.5 py-1 text-[11px] font-bold text-red-300"><Flag className="h-3 w-3" /> {reportCountByEvent[event.id]}</span>
                      )}
                      <span className="rounded-full bg-red-500/15 px-2.5 py-1 text-[11px] font-bold text-red-300">LIVE</span>
                    </span>
                  </button>
                ))}
                {liveMonitorEvents.length === 0 && <p className="px-5 py-12 text-center text-sm text-gray-500">No events are live right now.</p>}
              </div>
            </Section>
          </div>
        )}

        {activeTab === "events" && (
          <div className="space-y-6" data-testid="admin-events-section">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl font-extrabold">Events</h1>
                <p className="mt-1 text-sm text-gray-400">View every platform event, terminate an active event, or permanently delete it.</p>
              </div>
              <button
                type="button"
                onClick={() => loadResource("events", true)}
                disabled={resourceLoading.events}
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[.04] px-3 py-2 text-xs font-bold text-gray-300 hover:bg-white/[.08] disabled:opacity-50"
                data-testid="refresh-admin-events"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${resourceLoading.events ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard featured label="All events" value={events.length} detail="Across every creator" />
              <StatCard label="Live now" value={events.filter((event) => String(event.status || "").toLowerCase() === "live").length} accent="text-red-400" detail="Can be terminated immediately" />
              <StatCard label="Completed" value={events.filter((event) => String(event.status || "").toLowerCase() === "completed").length} accent="text-green-400" detail="Retained until deleted" />
            </div>

            <Section title="Deleted-event recovery" detail="Find retained production evidence from events deleted before automatic archives were enabled.">
              <div className="grid gap-3 p-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_190px_auto]">
                <input
                  type="email"
                  value={deletedRecoveryEmail}
                  onChange={(event) => setDeletedRecoveryEmail(event.target.value)}
                  placeholder="Creator email"
                  className="admin-field"
                  data-testid="deleted-recovery-email"
                />
                <input
                  type="text"
                  value={deletedRecoveryTitle}
                  onChange={(event) => setDeletedRecoveryTitle(event.target.value)}
                  placeholder="Event title (optional)"
                  className="admin-field"
                  data-testid="deleted-recovery-title"
                />
                <input
                  type="text"
                  value={deletedRecoveryDate}
                  onChange={(event) => setDeletedRecoveryDate(event.target.value)}
                  placeholder="YYYY-MM-DD (optional)"
                  className="admin-field"
                  data-testid="deleted-recovery-date"
                />
                <button
                  type="button"
                  onClick={inspectDeletedEvents}
                  disabled={deletedRecoveryBusy || !deletedRecoveryEmail.trim()}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-2 text-xs font-bold text-blue-300 hover:bg-blue-500/20 disabled:opacity-50"
                  data-testid="inspect-deleted-events"
                >
                  <Search className="h-3.5 w-3.5" /> {deletedRecoveryBusy ? "Inspecting…" : "Inspect"}
                </button>
              </div>
              {deletedRecovery && (
                <div className="border-t border-white/[.06] px-5 py-4" data-testid="deleted-recovery-results">
                  <p className="text-sm font-semibold text-white">{deletedRecovery.creator?.username || deletedRecovery.creator?.name} · {deletedRecovery.creator?.email}</p>
                  <p className="mt-1 text-xs text-gray-500">{deletedRecovery.candidates?.length || 0} deleted candidate(s) found</p>
                  <div className="mt-3 space-y-3">
                    {(deletedRecovery.candidates || []).map((candidate) => (
                      <div key={candidate.event_id} className="rounded-xl border border-white/[.08] bg-white/[.03] p-3">
                        <p className="font-mono text-xs text-blue-300">{candidate.event_id}</p>
                        <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-[11px] leading-relaxed text-gray-400">{JSON.stringify(candidate.evidence, null, 2)}</pre>
                        {deletedRecoveryTitle.trim() && deletedRecoveryDate && (
                          <button
                            type="button"
                            onClick={() => restoreDeletedEvent(candidate)}
                            disabled={deletedRecoveryBusy}
                            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2 text-xs font-bold text-green-300 hover:bg-green-500/20 disabled:opacity-50"
                            data-testid={`restore-deleted-event-${candidate.event_id}`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" /> Restore event and report
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            <Section title="All events" detail={`${filteredEvents.length} of ${events.length} events shown`}>
              <div className="grid gap-3 border-b border-white/[.06] p-4 sm:grid-cols-[minmax(0,1fr)_190px]">
                <label className="relative block">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  <input
                    type="search"
                    value={eventQuery}
                    onChange={(event) => setEventQuery(event.target.value)}
                    placeholder="Search title, event ID, creator, or email"
                    className="admin-field pl-10"
                    data-testid="admin-event-search"
                  />
                </label>
                <label>
                  <span className="sr-only">Filter events by status</span>
                  <select
                    value={eventStatus}
                    onChange={(event) => setEventStatus(event.target.value)}
                    className="admin-field"
                    data-testid="admin-event-status-filter"
                  >
                    <option value="all">All statuses</option>
                    <option value="live">Live</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="draft">Draft</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </label>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1040px] text-sm">
                  <thead className="bg-[#141416] text-left text-[11px] font-bold uppercase tracking-wider text-gray-400"><tr>
                    <th className="px-5 py-3">Event</th><th className="px-5 py-3">Creator</th><th className="px-5 py-3">Schedule</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Tickets</th><th className="px-5 py-3">Gross</th><th className="px-5 py-3 text-right">Actions</th>
                  </tr></thead>
                  <tbody className="divide-y divide-white/[.05]">
                    {filteredEvents.map((event) => {
                      const normalizedStatus = String(event.status || "scheduled").toLowerCase();
                      const canTerminate = !["completed", "cancelled"].includes(normalizedStatus);
                      return <tr key={event.id} className="hover:bg-white/[.02]">
                        <td className="px-5 py-4"><p className="font-semibold text-white">{event.title || "Untitled event"}</p><p className="mt-1 max-w-[260px] truncate font-mono text-[10px] text-gray-600">{event.id}</p></td>
                        <td className="px-5 py-4"><p className="font-semibold text-gray-300">{event.creator?.name || "Unknown creator"}</p><p className="mt-1 text-xs text-gray-500">{event.creator?.email}</p></td>
                        <td className="px-5 py-4 text-xs text-gray-400"><p>{formatDateMMDDYear(event.date) || "Date not set"}</p><p className="mt-1">{event.start_time || event.time || "Time not set"}</p></td>
                        <td className="px-5 py-4"><span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold capitalize ${normalizedStatus === "live" ? "bg-red-500/15 text-red-300" : normalizedStatus === "completed" ? "bg-green-500/15 text-green-300" : "bg-gray-500/15 text-gray-300"}`}>{normalizedStatus}</span></td>
                        <td className="px-5 py-4 text-gray-300">{event.ticket_count || 0}</td>
                        <td className="px-5 py-4 font-bold text-white">{money(event.total_revenue)}</td>
                        <td className="whitespace-nowrap px-5 py-4 text-right">
                          {canTerminate && (
                            <button
                              type="button"
                              onClick={() => endAdminEvent(event)}
                              disabled={Boolean(eventBusy)}
                              className="mr-2 inline-flex items-center gap-1.5 rounded-lg border border-orange-500/25 bg-orange-500/10 px-3 py-2 text-xs font-bold text-orange-300 transition hover:bg-orange-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                              data-testid={`terminate-event-${event.id}`}
                            >
                              <Square className="h-3.5 w-3.5" />
                              {eventBusy === `${event.id}:end` ? "Terminating…" : "Terminate"}
                            </button>
                          )}
                          {canCreatorDeleteEvent(event) && <button
                            type="button"
                            onClick={() => deleteAdminEvent(event)}
                            disabled={Boolean(eventBusy)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2 text-xs font-bold text-red-300 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                            data-testid={`delete-event-${event.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            {eventBusy === `${event.id}:delete` ? "Deleting…" : "Delete"}
                          </button>}
                        </td>
                      </tr>;
                    })}
                    {filteredEvents.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-gray-500">No events match this search and status filter.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Section>
          </div>
        )}

        {activeTab === "payouts" && (
          <div className="space-y-6" data-testid="admin-payouts-section">
            <div><h1 className="text-2xl font-extrabold">Payouts</h1><p className="mt-1 text-sm text-gray-400">Automatic payouts run 24 hours after each show. Manual release remains available as a backup.</p></div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard label="Awaiting payout" value={money(pendingPayoutTotal)} accent="text-green-400" detail={`${pendingPayouts.length} eligible events`} />
              <StatCard label="Refund holds" value={money(heldRefundTotal)} accent="text-red-400" detail={`${pendingRefunds.length} decisions required`} />
              <StatCard label="Gross event revenue" value={money(stats?.revenue?.total)} />
              <StatCard label="Platform share" value={money(stats?.revenue?.platform_earnings)} accent="text-blue-400" />
            </div>
            <Section
              title="Financial recovery"
              detail="Reconcile the live Stripe account against ShowMeLive, then restore missing ledger records without creating charges, transfers, refunds, or payouts."
              action={(
                <button
                  type="button"
                  onClick={previewFinancialRecovery}
                  disabled={financialRecoveryBusy}
                  className="inline-flex items-center gap-2 rounded-xl border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs font-bold text-blue-300 hover:bg-blue-500/20 disabled:opacity-50"
                  data-testid="preview-financial-recovery"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${financialRecoveryBusy ? "animate-spin" : ""}`} />
                  {financialRecoveryBusy ? "Checking Stripe…" : "Run read-only preview"}
                </button>
              )}
            >
              <div className="space-y-4 p-5">
                {!financialRecovery && !financialRecoveryResult && (
                  <p className="text-sm text-gray-400">No recovery has been applied. Run the preview to verify every live Stripe payment, completed creator transfer, and remaining viewer credit.</p>
                )}
                {financialRecovery && (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <StatCard label="Paid sessions" value={financialRecovery.counts?.paid_checkout_sessions || 0} detail={money(financialRecovery.amounts?.gross_payments)} />
                      <StatCard label="Creator transfers" value={financialRecovery.counts?.completed_creator_transfers || 0} detail={money(financialRecovery.amounts?.creator_transfers)} accent="text-green-400" />
                      <StatCard label="Recovered events" value={financialRecovery.counts?.events || 0} detail={`${financialRecovery.counts?.tickets || 0} tickets`} />
                      <StatCard label="Viewer credits" value={money(financialRecovery.amounts?.outstanding_viewer_credits)} detail={`${financialRecovery.counts?.viewer_wallets || 0} wallets`} accent="text-blue-400" />
                    </div>
                    <div className={`rounded-xl border p-4 ${financialRecovery.safe_to_apply ? "border-green-500/25 bg-green-500/10" : "border-red-500/25 bg-red-500/10"}`}>
                      <p className={`text-sm font-bold ${financialRecovery.safe_to_apply ? "text-green-300" : "text-red-300"}`}>
                        {financialRecovery.safe_to_apply ? "Reconciliation passed" : "Recovery blocked"}
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-gray-300">
                        Stripe writes: {financialRecovery.stripe_writes || 0}. Processing fees: {money(financialRecovery.amounts?.processing_fees)}. Unresolved records: {financialRecovery.unresolved?.length || 0}.
                      </p>
                    </div>
                    {financialRecovery.unresolved?.length > 0 && (
                      <div className="rounded-xl border border-red-500/20 bg-black/20 p-4" data-testid="financial-recovery-unresolved">
                        <p className="text-xs font-bold uppercase tracking-wider text-red-300">Records requiring review</p>
                        <ul className="mt-3 space-y-2 text-xs text-gray-300">
                          {financialRecovery.unresolved.map((item, index) => (
                            <li key={`${item.kind || "record"}-${index}`} className="rounded-lg bg-white/[.03] px-3 py-2">
                              {item.kind || "record"}
                              {item.email ? ` · ${item.email}` : ""}
                              {item.name ? ` · ${item.name}` : ""}
                              {item.detail ? ` · ${item.detail}` : ""}
                              {item.existing_spend_count ? ` · ${item.existing_spend_count} existing wallet spends` : ""}
                              {item.unknown_session_count ? ` · ${item.unknown_session_count} unrecognized wallet sessions` : ""}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={applyFinancialRecovery}
                      disabled={financialRecoveryBusy || !financialRecovery.safe_to_apply}
                      className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-40"
                      data-testid="apply-financial-recovery"
                    >
                      <ShieldCheck className="h-4 w-4" /> Restore verified records
                    </button>
                  </>
                )}
                {financialRecoveryResult && (
                  <div className="rounded-xl border border-green-500/25 bg-green-500/10 p-4" data-testid="financial-recovery-complete">
                    <p className="text-sm font-bold text-green-300">Recovery {financialRecoveryResult.status === "already_completed" ? "already completed" : "completed"}</p>
                    <p className="mt-1 text-xs text-gray-300">The ShowMeLive ledger now reflects the verified Stripe payments and completed creator transfers. No Stripe money movement was performed.</p>
                  </div>
                )}
              </div>
            </Section>
            <Section
              title="Recovered payments from the previous Stripe account"
              detail="Every retained charge is preserved here. These amounts stay on hold until the deleted event's creator and title are verified."
            >
              <div className="space-y-4 p-5" data-testid="historical-financial-recovery">
                {!historicalFinancialRecovery && (
                  <p className="text-sm text-gray-400">
                    Loading the previous-account payment archive…
                  </p>
                )}
                {historicalFinancialRecovery && (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                      <StatCard label="Recovered charges" value={historicalFinancialRecovery.counts?.payments || 0} detail={money(historicalFinancialRecovery.amounts?.gross_amount)} />
                      <StatCard label="Stripe fees" value={money(historicalFinancialRecovery.amounts?.processing_fee)} accent="text-yellow-300" />
                      <StatCard label="Net receipts" value={money(historicalFinancialRecovery.amounts?.net_receipts)} accent="text-blue-300" />
                      <StatCard label="Creator reserve" value={money(historicalFinancialRecovery.amounts?.creator_share)} accent="text-green-300" detail="80% of net" />
                      <StatCard label="Event groups" value={historicalFinancialRecovery.counts?.event_groups || 0} detail="Assignment required" />
                    </div>
                    <div className="rounded-xl border border-yellow-500/25 bg-yellow-500/[.08] p-4">
                      <p className="text-sm font-bold text-yellow-200">Payout safety hold is active</p>
                      <p className="mt-1 text-xs leading-relaxed text-gray-300">
                        Stripe preserved the event IDs and exact amounts, but not the deleted event titles or creators. No transfer has been created and these records are not included in automatic payouts until ownership is verified.
                      </p>
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-white/[.07]">
                      <table className="w-full min-w-[980px] text-sm">
                        <thead className="bg-[#141416] text-left text-[11px] font-bold uppercase tracking-wider text-gray-400"><tr>
                          <th className="px-4 py-3">Recovered event ID</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Charges</th><th className="px-4 py-3">Gross</th><th className="px-4 py-3">Stripe fees</th><th className="px-4 py-3">Creator reserve</th><th className="px-4 py-3">Payment period</th><th className="px-4 py-3">Status</th>
                        </tr></thead>
                        <tbody className="divide-y divide-white/[.05]">
                          {(historicalFinancialRecovery.claims || []).map((claim) => <tr key={claim.event_id} className="hover:bg-white/[.02]">
                            <td className="px-4 py-3 font-mono text-xs text-gray-300">{claim.event_id}</td>
                            <td className="px-4 py-3 text-gray-300">{(claim.categories || []).join(", ") || "Unspecified"}</td>
                            <td className="px-4 py-3 text-gray-300">{claim.payment_count || 0}<p className="mt-1 text-[11px] text-gray-500">{claim.ticket_count || 0} tickets · {claim.tip_count || 0} tips</p></td>
                            <td className="px-4 py-3 font-semibold text-white">{money(Number(claim.gross_amount_cents || 0) / 100)}</td>
                            <td className="px-4 py-3 text-yellow-300">{money(Number(claim.processing_fee_cents || 0) / 100)}</td>
                            <td className="px-4 py-3 font-bold text-green-300">{money(Number(claim.creator_share_cents || 0) / 100)}</td>
                            <td className="px-4 py-3 text-xs text-gray-400">{dateTime(claim.first_payment_at)}<br />to {dateTime(claim.last_payment_at)}</td>
                            <td className="px-4 py-3"><span className="rounded-full border border-yellow-500/25 bg-yellow-500/10 px-2.5 py-1 text-[11px] font-bold text-yellow-200">Needs creator</span></td>
                          </tr>)}
                          {(historicalFinancialRecovery.claims || []).length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-gray-500">No previous-account payment evidence is waiting.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </Section>
            <Section title="Event payouts" detail="24-hour post-show automation is primary. Manual payout is a backup.">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[780px] text-sm">
                  <thead className="bg-[#141416] text-left text-[11px] font-bold uppercase tracking-wider text-gray-400"><tr>
                    <th className="px-5 py-3">Creator / Event</th><th className="px-5 py-3">Net receipts</th><th className="px-5 py-3">Creator 80% gross</th><th className="px-5 py-3">Held</th><th className="px-5 py-3">Available</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Backup</th>
                  </tr></thead>
                  <tbody className="divide-y divide-white/[.05]">
                    {events.map((event) => {
                      const revenue = Number(event.total_revenue || 0);
                      const netReceipts = Number(event.net_receipts ?? revenue);
                      const creatorNetShare = Number(event.creator_earnings ?? (netReceipts * 0.8));
                      const held = Number(event.refund_hold_amount || 0);
                      const available = Number(event.available_creator_earnings ?? creatorNetShare);
                      return <tr key={event.id} className="hover:bg-white/[.02]">
                        <td className="px-5 py-4"><p className="font-semibold">{event.title}</p><p className="mt-1 text-xs text-gray-500">{event.creator?.name || "Creator"}</p></td>
                        <td className="px-5 py-4 text-gray-300">{money(netReceipts)}</td><td className="px-5 py-4 font-bold text-green-400">{money(creatorNetShare)}</td><td className="px-5 py-4 font-semibold text-red-400">{money(held)}</td><td className="px-5 py-4 text-gray-200">{money(available)}</td>
                        <td className="px-5 py-4"><StatusBadge active={event.payout_processed} activeLabel="Released" inactiveLabel={payoutStatusLabel(event, revenue, held)} />{event.payout_error && <p className="mt-1 max-w-[260px] text-xs text-red-300">{event.payout_error}</p>}</td>
                        <td className="px-5 py-4 text-right"><button onClick={() => payout(event)} disabled={event.payout_processed || revenue <= 0 || held > 0 || payingId === event.id} className="rounded-lg border border-white/10 bg-white/[.05] px-3 py-2 text-xs font-bold text-gray-300 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40" data-testid={`payout-${event.id}`}>{payingId === event.id ? "Releasing…" : "Manual payout"}</button></td>
                      </tr>;
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          </div>
        )}

        {activeTab === "refunds" && (
          <div className="space-y-6" data-testid="admin-refunds-section">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div><h1 className="text-2xl font-extrabold">Refund queue</h1><p className="mt-1 text-sm text-gray-400">Requested amounts are automatically held from event profit until you decide.</p></div>
              <button onClick={() => Promise.all([loadResource("refunds", true), loadResource("events", true)])} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[.04] px-3 py-2 text-xs font-bold text-gray-300 hover:bg-white/[.08]"><RefreshCw className={`h-3.5 w-3.5 ${resourceLoading.refunds ? "animate-spin" : ""}`} /> Refresh</button>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard label="Pending decisions" value={pendingRefunds.length} accent="text-red-400" detail="Also shown in the notification bell" />
              <StatCard label="Profit currently held" value={money(heldRefundTotal)} accent="text-yellow-400" detail="Excluded from creator payout availability" />
              <StatCard label="Decided requests" value={refunds.length - pendingRefunds.length} detail={`${refunds.length} total refund requests`} />
            </div>
            <Section title="Viewer refund requests" detail="Approve returns money to the viewer. Reject releases the hold back to creator profit.">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] text-sm">
                  <thead className="bg-[#141416] text-left text-[11px] font-bold uppercase tracking-wider text-gray-400"><tr>
                    <th className="px-5 py-3">Viewer</th><th className="px-5 py-3">Event</th><th className="px-5 py-3">Reason</th><th className="px-5 py-3">Held amount</th><th className="px-5 py-3">Requested</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Decision</th>
                  </tr></thead>
                  <tbody className="divide-y divide-white/[.05]">
                    {refunds.map((refund) => {
                      const pending = refund.status === "pending";
                      return <tr key={refund.id} className="hover:bg-white/[.02]">
                        <td className="px-5 py-4"><p className="font-semibold">{refund.viewer?.username ? `@${refund.viewer.username.replace(/^@/, "")}` : (refund.viewer?.name || "Viewer")}</p><p className="mt-1 text-xs text-gray-500">{refund.viewer?.email}</p></td>
                        <td className="px-5 py-4"><p className="font-semibold">{refund.event?.title || "Event"}</p><p className="mt-1 text-xs text-gray-500">Ticket {String(refund.ticket_id || "").slice(0, 8)}</p></td>
                        <td className="max-w-xs px-5 py-4 text-gray-400"><p className="line-clamp-2">{refund.reason}</p></td>
                        <td className="px-5 py-4 font-bold text-yellow-400">{money(refund.amount)}</td>
                        <td className="px-5 py-4 text-xs text-gray-400">{dateTime(refund.created_at)}</td>
                        <td className="px-5 py-4"><StatusBadge active={!pending} activeLabel={refund.status === "approved" ? "Approved" : "Rejected"} inactiveLabel="Pending" /></td>
                        <td className="whitespace-nowrap px-5 py-4 text-right">
                          {pending ? <>
                            <button onClick={() => decideRefund(refund, "approve")} disabled={Boolean(refundBusy)} className="mr-2 inline-flex items-center gap-1 rounded-lg bg-green-500/15 px-3 py-2 text-xs font-bold text-green-400 hover:bg-green-500/25 disabled:opacity-50" data-testid={`approve-refund-${refund.id}`}><Check className="h-3.5 w-3.5" /> {refundBusy === `${refund.id}:approve` ? "Approving..." : "Approve"}</button>
                            <button onClick={() => decideRefund(refund, "reject")} disabled={Boolean(refundBusy)} className="inline-flex items-center gap-1 rounded-lg bg-red-500/15 px-3 py-2 text-xs font-bold text-red-400 hover:bg-red-500/25 disabled:opacity-50" data-testid={`reject-refund-${refund.id}`}><X className="h-3.5 w-3.5" /> {refundBusy === `${refund.id}:reject` ? "Rejecting..." : "Reject"}</button>
                          </> : <span className="text-xs text-gray-500">{refund.decided_by || "Admin"}</span>}
                        </td>
                      </tr>;
                    })}
                    {refunds.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-gray-500">No refund requests are waiting.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Section>
          </div>
        )}

        {activeTab === "reports" && stats && (
          <div className="space-y-6" data-testid="admin-reports-section">
            <div>
              <h1 className="text-2xl font-extrabold">Reports</h1>
              <p className="mt-1 text-sm text-gray-400">Platform-wide event, ticket, revenue, and refund performance.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard featured label="Gross event revenue" value={money(stats.revenue.total)} detail={`${events.length} events reported`} />
              <StatCard label="Tickets issued" value={stats.tickets.total} accent="text-blue-400" detail="Across all platform events" />
              <StatCard label="Creator share queued" value={money(pendingPayoutTotal)} accent="text-green-400" detail="After platform fee and refund holds" />
              <StatCard label="Refund approval rate" value={`${refunds.length ? Math.round((refunds.filter((item) => item.status === "approved").length / refunds.length) * 100) : 0}%`} accent="text-yellow-400" detail={`${refunds.length} total decisions and requests`} />
            </div>
            {stripeBalanceDiagnostic && (
              <Section title="Live Stripe balance" detail="Read directly from the production Stripe account used by payout transfers.">
                <div className="grid gap-3 p-5 sm:grid-cols-3">
                  <StatCard label="Stripe available" value={money(((stripeBalanceDiagnostic.available || []).find((item) => item.currency === "usd")?.amount_cents || 0) / 100)} />
                  <StatCard label="Stripe pending" value={money(((stripeBalanceDiagnostic.pending || []).find((item) => item.currency === "usd")?.amount_cents || 0) / 100)} />
                  <StatCard label="Connect reserved" value={money(((stripeBalanceDiagnostic.connect_reserved || []).find((item) => item.currency === "usd")?.amount_cents || 0) / 100)} />
                </div>
              </Section>
            )}
            <Section
              title={`Live content reports (${contentReports.length})`}
              detail="The reported event, reporting viewer, selected reason, creator, and enforcement status."
              action={(
                <button
                  type="button"
                  onClick={() => loadResource("contentReports", true)}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[.04] px-3 py-2 text-xs font-bold text-gray-300 hover:bg-white/[.08]"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
              )}
            >
              <div className="overflow-x-auto" data-testid="admin-content-reports">
                <table className="w-full min-w-[1050px] text-sm">
                  <thead className="bg-[#141416] text-left text-[11px] font-bold uppercase tracking-wider text-gray-400"><tr>
                    <th className="px-5 py-3">Reported event</th><th className="px-5 py-3">Viewer who reported</th><th className="px-5 py-3">Reason</th><th className="px-5 py-3">Creator</th><th className="px-5 py-3">Time</th><th className="px-5 py-3">Status</th>
                  </tr></thead>
                  <tbody className="divide-y divide-white/[.05]">
                    {contentReports.map((report) => (
                      <tr key={report.id} className="hover:bg-white/[.02]">
                        <td className="px-5 py-4"><p className="font-semibold text-white">{report.event?.title || "Deleted or unavailable event"}</p><p className="mt-1 font-mono text-[10px] text-gray-600">{report.event_id}</p></td>
                        <td className="px-5 py-4"><p className="font-semibold text-gray-200">{report.reporter?.username ? `@${String(report.reporter.username).replace(/^@/, "")}` : (report.reporter?.name || "Viewer")}</p><p className="mt-1 text-xs text-gray-500">{report.reporter?.email || report.reporter_id}</p></td>
                        <td className="px-5 py-4 font-semibold text-red-200">{report.reason || "Other"}</td>
                        <td className="px-5 py-4"><p className="text-gray-300">{report.creator?.name || "Creator"}</p><p className="mt-1 text-xs text-gray-500">{report.creator?.email}</p></td>
                        <td className="px-5 py-4 text-xs text-gray-400">{dateTime(report.created_at || report.updated_at)}</td>
                        <td className="px-5 py-4"><StatusBadge active={report.status !== "received"} activeLabel={String(report.status || "reviewed").replaceAll("_", " ")} inactiveLabel={report.reporter_is_banned ? "Viewer blocked" : "Received"} /></td>
                      </tr>
                    ))}
                    {contentReports.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-gray-500">No live content reports have been submitted.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Section>
            <Section
              title="Verified data recovery"
              detail="Restore surviving records from the retained MongoDB database and production S3 bucket without changing Stripe, payouts, or live financial totals."
              action={(
                <button
                  type="button"
                  onClick={previewLegacyRecovery}
                  disabled={legacyRecoveryBusy}
                  className="inline-flex items-center gap-2 rounded-xl border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs font-bold text-blue-300 hover:bg-blue-500/20 disabled:opacity-50"
                  data-testid="preview-legacy-recovery"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${legacyRecoveryBusy ? "animate-spin" : ""}`} />
                  {legacyRecoveryBusy ? "Checking sources…" : "Run read-only preview"}
                </button>
              )}
            >
              <div className="space-y-4 p-5">
                {!legacyRecovery && !legacyRecoveryResult && (
                  <p className="text-sm text-gray-400">Run the preview to compare the retained database and production media bucket with the active site.</p>
                )}
                {legacyRecovery && (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                      <StatCard label="Upcoming events" value={legacyRecovery.counts?.upcoming_events || 0} />
                      <StatCard label="Promo codes" value={legacyRecovery.counts?.promo_codes || 0} accent="text-purple-300" />
                      <StatCard label="Archived reports" value={legacyRecovery.counts?.archived_event_reports || 0} accent="text-blue-300" />
                      <StatCard label="Automation flyers" value={legacyRecovery.counts?.automation_flyers || 0} />
                      <StatCard label="Media files" value={legacyRecovery.counts?.media_assets || 0} accent="text-green-300" />
                    </div>
                    {legacyRecovery.promo_codes?.length > 0 && (
                      <div className="rounded-xl border border-purple-500/20 bg-purple-500/[.06] p-4">
                        <p className="text-xs font-bold uppercase tracking-wider text-purple-300">Verified promo codes</p>
                        <p className="mt-2 text-sm text-gray-300">{legacyRecovery.promo_codes.map((item) => item.code).join(", ")}</p>
                      </div>
                    )}
                    {legacyRecovery.unavailable?.map((item) => (
                      <div key={item.kind} className="rounded-xl border border-yellow-500/20 bg-yellow-500/[.06] p-4">
                        <p className="text-sm font-bold capitalize text-yellow-300">{item.kind.replaceAll("_", " ")} not found</p>
                        <p className="mt-1 text-xs leading-relaxed text-gray-300">{item.reason}</p>
                      </div>
                    ))}
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-green-500/20 bg-green-500/[.06] p-4">
                      <div>
                        <p className="text-sm font-bold text-green-300">Financial ledger protected</p>
                        <p className="mt-1 text-xs text-gray-400">External-service writes: none · Payment and payout changes: none</p>
                      </div>
                      <button
                        type="button"
                        onClick={applyLegacyRecovery}
                        disabled={!legacyRecovery.can_apply || legacyRecoveryBusy}
                        className="rounded-xl bg-green-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-green-500 disabled:opacity-40"
                        data-testid="apply-legacy-recovery"
                      >
                        {legacyRecoveryArmed ? "Confirm restore" : "Restore verified records"}
                      </button>
                    </div>
                  </>
                )}
                {legacyRecoveryResult && (
                  <div className="rounded-xl border border-green-500/25 bg-green-500/10 p-4 text-sm text-green-200">
                    Recovery completed: {Object.entries(legacyRecoveryResult.counts || {}).map(([key, value]) => `${value} ${key.replaceAll("_", " ")}`).join(" · ")}
                  </div>
                )}
              </div>
            </Section>
            <Section
              title={`Recovered report archive (${recoveredReports.length})`}
              detail="Historical engagement is kept separate so older/test revenue cannot affect current creator payouts."
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[940px] text-sm">
                  <thead className="bg-[#141416] text-left text-[11px] font-bold uppercase tracking-wider text-gray-400"><tr>
                    <th className="px-5 py-3">Event</th><th className="px-5 py-3">Creator</th><th className="px-5 py-3">Viewers</th><th className="px-5 py-3">Messages</th><th className="px-5 py-3">Reactions</th><th className="px-5 py-3">Tips</th><th className="px-5 py-3">Protection</th>
                  </tr></thead>
                  <tbody className="divide-y divide-white/[.05]">
                    {recoveredReports.map((report) => <tr key={report.id} className="hover:bg-white/[.02]">
                      <td className="px-5 py-4"><p className="font-semibold text-white">{report.event?.title || "Untitled event"}</p><p className="mt-1 text-xs text-gray-500">{report.event?.date || "Date unavailable"}</p></td>
                      <td className="px-5 py-4 text-gray-300">{report.event?.creator_name || "Unknown creator"}</td>
                      <td className="px-5 py-4 text-gray-300">{report.engagement?.viewer_count || 0}</td>
                      <td className="px-5 py-4 text-gray-300">{report.engagement?.message_count || 0}</td>
                      <td className="px-5 py-4 text-gray-300">{report.engagement?.reaction_count || 0}</td>
                      <td className="px-5 py-4"><p className="font-semibold text-green-300">{money(report.tips?.total)}</p><p className="mt-1 text-xs text-gray-500">{report.tips?.count || 0} records</p></td>
                      <td className="px-5 py-4"><p className="text-gray-300">{report.content_protection?.security_events || 0} signals</p><p className="mt-1 text-xs text-gray-500">{report.content_protection?.violations || 0} violations</p></td>
                    </tr>)}
                    {recoveredReports.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-500">No recovered report archive yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Section>
            <Section
              title={`Banned viewers (${viewerBans.length})`}
              detail="Administrators can restore a viewer's access to every current and future event from the creator who issued the ban."
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm">
                  <thead className="bg-[#141416] text-left text-[11px] font-bold uppercase tracking-wider text-gray-400"><tr>
                    <th className="px-5 py-3">Viewer</th><th className="px-5 py-3">Creator</th><th className="px-5 py-3">Source event</th><th className="px-5 py-3">Banned</th><th className="px-5 py-3 text-right">Access</th>
                  </tr></thead>
                  <tbody className="divide-y divide-white/[.05]">
                    {viewerBans.map((ban) => {
                      const busyKey = `${ban.creator_id}:${ban.user_id}`;
                      return <tr key={busyKey} className="hover:bg-white/[.02]">
                        <td className="px-5 py-4"><p className="font-semibold">{ban.viewer?.username ? `@${String(ban.viewer.username).replace(/^@/, "")}` : (ban.viewer?.name || "Viewer")}</p><p className="mt-1 text-xs text-gray-500">{ban.viewer?.email || ban.user_id}</p></td>
                        <td className="px-5 py-4"><p className="font-semibold text-gray-200">{ban.creator?.name || "Creator"}</p><p className="mt-1 text-xs text-gray-500">{ban.creator?.email || ban.creator_id}</p></td>
                        <td className="px-5 py-4 text-gray-400">{ban.source_event?.title || "Event unavailable"}</td>
                        <td className="px-5 py-4 text-xs text-gray-400">{dateTime(ban.banned_at)}</td>
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => unbanViewer(ban)}
                            disabled={Boolean(unbanBusy)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-green-500/25 bg-green-500/10 px-3 py-2 text-xs font-bold text-green-300 transition hover:bg-green-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                            data-testid={`admin-unban-${ban.creator_id}-${ban.user_id}`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            {unbanBusy === busyKey ? "Restoring…" : "Unban"}
                          </button>
                        </td>
                      </tr>;
                    })}
                    {viewerBans.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-500">No active viewer bans.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Section>
          </div>
        )}

        {activeTab === "automations" && automations && (
          <div className="space-y-6" data-testid="admin-automations-section">
            <div><h1 className="text-2xl font-extrabold">Automations & protection</h1><p className="mt-1 text-sm text-gray-400">Control routine communication, creator payouts, viewer connection diagnostics, and ShowMeProtection from one place.</p></div>
            {!automations.email_provider_configured && (
              <div className="rounded-xl border border-yellow-500/25 bg-yellow-500/10 px-4 py-3 text-sm text-yellow-300">
                Email automations are ready but delivery requires the <code className="font-mono">RESEND_API_KEY</code> environment variable. Attempts are retained in the delivery audit.
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <AutomationCard
                icon={Mail}
                title="Automatic welcome emails"
                detail="Send a branded welcome email immediately after a new viewer or creator completes sign-up."
                enabled={automations.welcome_emails_enabled}
                busy={automationBusy === "welcome_emails_enabled"}
                onToggle={() => toggleAutomation("welcome_emails_enabled")}
                footer={`${automations.welcome_sent_count || 0} welcome emails sent`}
                testId="toggle-welcome-emails"
              />
              <AutomationCard
                icon={FileText}
                title="Monthly creator reports"
                detail="Email each creator a monthly summary containing that month's events, ticket counts, and revenue."
                enabled={automations.monthly_reports_enabled}
                busy={automationBusy === "monthly_reports_enabled"}
                onToggle={() => toggleAutomation("monthly_reports_enabled")}
                footer={`Next run: ${dateTime(automations.next_monthly_report_run)}`}
                testId="toggle-monthly-reports"
              />
              <AutomationCard
                icon={Megaphone}
                title="Campaigns"
                detail="Blast the saved promotional message and flyer to every eligible ShowMeLive account."
                enabled={automations.campaigns_enabled}
                busy={automationBusy === "campaigns_enabled"}
                onToggle={() => toggleAutomation("campaigns_enabled")}
                footer={automations.latest_campaign
                  ? `Latest: ${String(automations.latest_campaign.status || "queued").replaceAll("_", " ")} · ${automations.latest_campaign.sent_count || 0} sent`
                  : `${automations.campaign_sent_count || 0} campaign emails sent`}
                testId="toggle-campaigns"
              />
              <AutomationCard
                icon={Trophy}
                title="Top Monthly Viewers"
                detail="Email the top 10 ShowMeTithes senders and top 10 ShowMeTips senders from the previous month."
                enabled={automations.top_monthly_viewers_enabled}
                busy={automationBusy === "top_monthly_viewers_enabled"}
                onToggle={() => toggleAutomation("top_monthly_viewers_enabled")}
                footer={`Next run: ${dateTime(automations.next_top_monthly_viewers_run)}`}
                testId="toggle-top-monthly-viewers"
              />
            </div>

            <Section title="Email messages & flyers" detail="Edit the subject and message, then attach or replace the flyer used by each automation.">
              <div className="grid gap-4 p-5 xl:grid-cols-2">
                {EMAIL_AUTOMATIONS.map((automation) => (
                  <EmailTemplateEditor
                    key={automation.key}
                    automation={automation}
                    draft={emailTemplateDrafts[automation.key] || automations.email_templates?.[automation.key]}
                    busy={Boolean(emailTemplateBusy?.startsWith(`${automation.key}:`))}
                    onChange={(field, value) => updateEmailDraft(automation.key, field, value)}
                    onSave={() => saveEmailTemplate(automation.key)}
                    onUpload={(file) => uploadEmailFlyer(automation.key, file)}
                    onRemoveFlyer={() => removeEmailFlyer(automation.key)}
                    onSendCampaign={sendCampaign}
                    campaignBusy={campaignBusy}
                  />
                ))}
              </div>
            </Section>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <AutomationCard
                icon={Clock3}
                title="24-hour post-show payout"
                detail="Automatically release the creator's full 80% of gross receipts 24 hours after the show ends. ShowMeLive covers Stripe fees; refund holds pause payout."
                enabled={automations.automatic_payouts_enabled}
                busy={automationBusy === "automatic_payouts_enabled"}
                onToggle={() => toggleAutomation("automatic_payouts_enabled")}
                footer={`Hourly check · Manual payout is backup`}
                testId="toggle-automatic-payouts"
              />
              <AutomationCard
                icon={Radio}
                title="Viewer buffer (AWS IVS)"
                detail="Route viewer video through the buffered AWS IVS layer. Turn this off to test the direct LiveKit connection path."
                enabled={automations.viewer_buffer_enabled}
                busy={automationBusy === "viewer_buffer_enabled"}
                onToggle={() => toggleAutomation("viewer_buffer_enabled")}
                footer={automations.viewer_buffer_enabled ? "Buffered viewer path is active" : "Direct LiveKit path is active"}
                testId="toggle-viewer-buffer"
              />
              <AutomationCard
                icon={ShieldCheck}
                title="ShowMeProtection"
                detail="Control screenshot, screen-recording, print, tab-switch, and protected-content enforcement across live events."
                enabled={automations.showme_protection_enabled}
                busy={automationBusy === "showme_protection_enabled"}
                onToggle={() => toggleAutomation("showme_protection_enabled")}
                footer={automations.showme_protection_enabled ? "Protection is active" : "Disabled for testing"}
                testId="toggle-showme-protection"
              />
            </div>
            <Section title="Automation safeguards" detail="Operational rules applied before any automated action.">
              <div className="grid gap-4 p-5 sm:grid-cols-3">
                <div className="rounded-xl bg-white/[.03] p-4"><ShieldCheck className="mb-3 h-5 w-5 text-blue-400" /><p className="text-sm font-bold">Audited changes</p><p className="mt-1 text-xs leading-relaxed text-gray-500">Every toggle change records the administrator, timestamp, and new setting.</p></div>
                <div className="rounded-xl bg-white/[.03] p-4"><RotateCcw className="mb-3 h-5 w-5 text-yellow-400" /><p className="text-sm font-bold">Refund-aware payouts</p><p className="mt-1 text-xs leading-relaxed text-gray-500">Any pending refund hold pauses both automatic and manual event payout.</p></div>
                <div className="rounded-xl bg-white/[.03] p-4"><WalletCards className="mb-3 h-5 w-5 text-green-400" /><p className="text-sm font-bold">No duplicate payout</p><p className="mt-1 text-xs leading-relaxed text-gray-500">Completed events and tickets are marked before the next hourly check.</p></div>
              </div>
            </Section>
          </div>
        )}

        {activeTab === "creators" && (
          <div className="space-y-6" data-testid="admin-creators-section">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div><h1 className="text-2xl font-extrabold">Creators</h1><p className="mt-1 text-sm text-gray-400">Account directory and platform access status.</p></div>
              <label className="flex h-11 w-full items-center gap-2 rounded-xl border border-white/10 bg-white/[.04] px-3 sm:w-72">
                <Search className="h-4 w-4 text-gray-500" /><input aria-label="Search creators" value={creatorQuery} onChange={(event) => setCreatorQuery(event.target.value)} placeholder="Search creators…" className="w-full bg-transparent text-sm text-white outline-none placeholder:text-gray-600" />
              </label>
            </div>
            <Section title={`Creator directory (${filteredCreators.length})`}>
              <div className="overflow-x-auto"><table className="w-full min-w-[820px] text-sm">
                <thead className="bg-[#141416] text-left text-[11px] font-bold uppercase tracking-wider text-gray-400"><tr><th className="px-5 py-3">Creator</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Events</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr></thead>
                <tbody className="divide-y divide-white/[.05]">
                  {filteredCreators.map((creator) => {
                    const eventCount = events.filter((event) => event.creator_id === creator.id).length;
                    return <tr key={creator.id} className="hover:bg-white/[.02]">
                      <td className="px-5 py-4"><div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-blue-900 font-bold">{(creator.username || creator.name || "C").charAt(0).toUpperCase()}</span><div><p className="font-semibold">{creator.username ? `@${creator.username.replace(/^@/, "")}` : creator.name}</p><p className="mt-1 text-xs text-gray-500">{creator.email}</p></div></div></td>
                      <td className="px-5 py-4 capitalize text-gray-300">{creator.role || "member"}</td>
                      <td className="px-5 py-4 text-gray-300">{eventCount}</td>
                      <td className="px-5 py-4"><StatusBadge active={!creator.is_blocked} activeLabel="Active" inactiveLabel="Blocked" /></td>
                      <td className="px-5 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => openCreatorDelete(creator)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2 text-xs font-bold text-red-300 transition hover:bg-red-500/20"
                          aria-label={`Delete profile for ${creator.email}`}
                          data-testid={`delete-creator-${creator.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete profile
                        </button>
                      </td>
                    </tr>;
                  })}
                  {filteredCreators.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-gray-500">No creators match this search.</td></tr>}
                </tbody>
              </table></div>
            </Section>
          </div>
        )}

        {activeTab === "social" && <SocialMediaCommandCenter />}

        {activeTab === "promos" && (
          <div className="space-y-6" data-testid="admin-promo-codes-section">
            <div><h1 className="text-2xl font-extrabold">Promo codes</h1><p className="mt-1 text-sm text-gray-400">Create and manage rewards for tickets, Pro Mode, or the entire platform.</p></div>
            <Section title="Create a promo code" detail="Codes are automatically converted to uppercase.">
              <form onSubmit={createPromo} className="grid gap-4 p-5 md:grid-cols-2 xl:grid-cols-4">
                <PromoField label="Code"><input required value={promoForm.code} onChange={(event) => setPromoField("code", event.target.value)} placeholder="LAUNCH50" className="admin-field uppercase" /></PromoField>
                <PromoField label="Description"><input value={promoForm.description} onChange={(event) => setPromoField("description", event.target.value)} placeholder="Launch reward" className="admin-field" /></PromoField>
                <PromoField label="Discount type"><select value={promoForm.discount_type} onChange={(event) => setPromoField("discount_type", event.target.value)} className="admin-field"><option value="percentage">Percentage off</option><option value="fixed">Fixed amount off</option></select></PromoField>
                <PromoField label="Value"><input required type="number" min="0" step="any" value={promoForm.discount_value} onChange={(event) => setPromoField("discount_value", event.target.value)} placeholder={promoForm.discount_type === "percentage" ? "20" : "10.00"} className="admin-field" /></PromoField>
                <PromoField label="Applies to"><select value={promoForm.applies_to} onChange={(event) => setPromoField("applies_to", event.target.value)} className="admin-field"><option value="all">Everything</option><option value="ticket">Tickets</option><option value="pro_mode">Pro Mode</option></select></PromoField>
                <PromoField label="Maximum uses"><input type="number" min="1" value={promoForm.max_uses} onChange={(event) => setPromoField("max_uses", event.target.value)} placeholder="Unlimited" className="admin-field" /></PromoField>
                <PromoField label="Expiration date"><input type="date" value={promoForm.expiration_date} onChange={(event) => setPromoField("expiration_date", event.target.value)} className="admin-field [color-scheme:dark]" /></PromoField>
                <div className="flex items-end"><button type="submit" disabled={promoBusy} className="h-11 w-full rounded-xl bg-gradient-to-br from-blue-600 to-blue-900 text-sm font-bold shadow-[0_10px_25px_rgba(37,99,235,.25)] hover:from-blue-500 hover:to-blue-800 disabled:opacity-50" data-testid="create-promo">{promoBusy ? "Creating…" : "Create promo code"}</button></div>
              </form>
            </Section>
            <Section title={`Active promo library (${promos.length})`} detail="Enable, pause, or remove codes at any time.">
              <div className="overflow-x-auto"><table className="w-full min-w-[760px] text-sm">
                <thead className="bg-[#141416] text-left text-[11px] font-bold uppercase tracking-wider text-gray-400"><tr><th className="px-5 py-3">Code</th><th className="px-5 py-3">Discount</th><th className="px-5 py-3">Applies to</th><th className="px-5 py-3">Uses</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr></thead>
                <tbody className="divide-y divide-white/[.05]">
                  {promos.map((promo) => <tr key={promo.id} className="hover:bg-white/[.02]"><td className="px-5 py-4"><span className="rounded-lg border border-dashed border-purple-500/40 bg-purple-500/10 px-3 py-2 font-mono text-xs font-bold tracking-wide text-purple-300">{promo.code}</span><p className="mt-2 max-w-xs truncate text-xs text-gray-500">{promo.description}</p></td><td className="px-5 py-4 font-semibold">{promo.discount_type === "percentage" ? `${promo.discount_value}% off` : `${money(promo.discount_value)} off`}</td><td className="px-5 py-4 capitalize text-gray-400">{(promo.applies_to || "all").replace("_", " ")}</td><td className="px-5 py-4 text-gray-400">{promo.current_uses || 0}{promo.max_uses ? ` / ${promo.max_uses}` : " / Unlimited"}</td><td className="px-5 py-4"><StatusBadge active={promo.is_active !== false} /></td><td className="px-5 py-4 text-right whitespace-nowrap"><button onClick={() => togglePromo(promo)} className="mr-2 rounded-lg border border-white/10 bg-white/[.05] px-3 py-2 text-xs font-bold text-gray-300 hover:bg-white/10">{promo.is_active !== false ? "Pause" : "Enable"}</button><button onClick={() => deletePromo(promo)} className="rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2 text-xs font-bold text-red-300 hover:bg-red-500/20">Delete</button></td></tr>)}
                  {promos.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-500">No promo codes yet. Create the first one above.</td></tr>}
                </tbody>
              </table></div>
            </Section>
          </div>
        )}
        </main>
      </div>

      {creatorPendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
          role="presentation"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeCreatorDelete(); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-creator-title"
            className="w-full max-w-lg rounded-2xl border border-red-500/25 bg-[#161618] p-6 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-500/15 text-red-400"><Trash2 className="h-5 w-5" /></span>
              <div>
                <h2 id="delete-creator-title" className="text-lg font-extrabold">Delete creator profile?</h2>
                <p className="mt-2 text-sm leading-relaxed text-gray-400">
                  This permanently removes the creator's profile, email, notifications, and active sessions. Their historical events, tithes, payouts, and reports are retained.
                </p>
              </div>
            </div>
            <div className="mt-5 rounded-xl border border-white/[.08] bg-white/[.03] p-4">
              <p className="text-sm font-semibold text-white">{creatorPendingDelete.username ? `@${creatorPendingDelete.username.replace(/^@/, "")}` : creatorPendingDelete.name}</p>
              <p className="mt-1 text-xs text-gray-400">{creatorPendingDelete.email}</p>
            </div>
            <label className="mt-5 block">
              <span className="text-xs font-semibold text-gray-300">Type the creator's email to confirm</span>
              <input
                type="email"
                autoFocus
                value={creatorDeleteConfirmation}
                onChange={(event) => setCreatorDeleteConfirmation(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") closeCreatorDelete();
                }}
                placeholder={creatorPendingDelete.email}
                className="admin-field mt-2"
                data-testid="delete-creator-confirmation"
              />
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={closeCreatorDelete} disabled={creatorDeleteBusy} className="rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-gray-300 hover:bg-white/5 disabled:opacity-50">Cancel</button>
              <button
                type="button"
                onClick={deleteCreatorProfile}
                disabled={creatorDeleteBusy || creatorDeleteConfirmation.trim().toLowerCase() !== String(creatorPendingDelete.email || "").trim().toLowerCase()}
                className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="confirm-delete-creator"
              >
                <Trash2 className="h-4 w-4" /> {creatorDeleteBusy ? "Deleting…" : "Delete creator profile"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-field {
          height: 44px;
          width: 100%;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,.1);
          background: rgba(255,255,255,.04);
          padding: 0 12px;
          color: white;
          font-size: 14px;
          outline: none;
        }
        .admin-field:focus { border-color: #3b82f6; background: rgba(59,130,246,.06); }
        .admin-field::placeholder { color: #4b5563; }
        .admin-field option { background: #141416; color: white; }
      `}</style>
    </div>
  );
};

const PromoField = ({ label, children }) => (
  <label className="flex flex-col gap-2">
    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{label}</span>
    {children}
  </label>
);

export default AdminDashboard;
